import { MinusOutlineIcon, PlusOutlineIcon } from '@carboncopyco/icons';
import type { ZoomFactor } from '@carboncopyco/types/desktop';
import { Button } from '@carboncopyco/ui';
import { useEffect, useRef, useState } from 'react';
import { AppEventsEnum } from '../../../shared/enums/AppEventsEnum';
import useElectronListener from '../../hooks/useElectronListener';
import { useTabs } from '../../store/context';

const ZoomPopup = () => {
  const { activeGlobalTabId } = useTabs();
  const [zoom, setZoom] = useState<ZoomFactor | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [hovering, setHovering] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    setHovering(true);
  };

  const handleMouseLeave = () => {
    setHovering(false);
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
      setShowPopup(false);
    }
  };

  const handleShowZoomPopup = () => {
    setShowPopup(true);
    if (!zoom) setZoom({ value: 1, min: 0.2, max: 5 });
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (showPopup) {
      timeout = setTimeout(() => {
        if (!hovering) {
          setShowPopup(false);
        }
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [showPopup, hovering, zoom]);

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  useEffect(() => {
    window.dsDesktop.app.onInnerZoomChange((value) => {
      setShowPopup(true);
      setZoom(value);
    });
  }, []);

  useEffect(() => {
    setShowPopup(false);
  }, [activeGlobalTabId]);

  useElectronListener(
    AppEventsEnum.MAIN_TO_RENDERER_SHOW_ZOOM_POPUP,
    handleShowZoomPopup
  );

  const zoomValue = ((zoom?.value || 1) * 100).toFixed();

  if (!showPopup) return null;
  return (
    <div
      className="fixed top-4 right-80 z-200 no-drag"
      ref={popupRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="max-w-min flex items-center justify-center p-2 rounded-md bg-ds-accent3-100 shadow-ds-edit-tool space-x-1">
        <span className="text-ds-natural-600 text-ds-400 text-center w-[72px] block">
          {zoomValue}%
        </span>
        <Button
          secondary
          link
          disabled={!zoom || zoom.value >= zoom.max}
          onClick={() => {
            window.dsDesktop.app.innerZoom(0.1);
          }}
          icon={<PlusOutlineIcon />}
        />
        <Button
          secondary
          link
          disabled={!zoom || zoom.value <= zoom.min}
          onClick={() => {
            window.dsDesktop.app.innerZoom(-0.1);
          }}
          icon={<MinusOutlineIcon />}
        />
        <Button
          secondary
          disabled={!zoom || zoom.value === 1}
          onClick={() => {
            window.dsDesktop.app.innerZoom(1);
          }}
          label="Reset"
        />
      </div>
    </div>
  );
};

export default ZoomPopup;
